import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  RADE_MAP_CARD,
  closeCard,
  setCardsToRender,
  setMapCardData,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { isValidInternationalPhoneNumber } from '@rsos/utils/internationalPhoneNumberUtils';
import { normalizeInlineRADE } from '@rsos/utils/normalizeRADE';
import fetchEmergencySession from '../../../../actions/fetchEmergencySession';
import type { RootState } from '../../../../app';
import {
  clearAdrLookup,
  resolveAdrLookup,
  selectLookup,
  startAdrLookup,
} from '../../RADECard/radeSlice';
import type { RadeCardItem, RadeLookupState } from '../../RADECard/radeSlice';

type EmergencyDataDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

/**
 * Parse an ADR (phone-number RAD-e) lookup term. Uses
 * `isValidInternationalPhoneNumber` (libphonenumber-js) to accept any
 * valid phone number, defaulting to US for unqualified inputs:
 *
 *   - `2223334444`
 *   - `222-333-4444`
 *   - `12223334444`
 *   - `1-222-333-4444`
 *   - international numbers (e.g. `+44...`)
 *
 * Returns the digits-only form for the lookup, or `null` if the term
 * isn't a recognizable phone shape.
 *
 * Mirroring the legacy ADR path, this function is intentionally
 * permission-agnostic — the API filters by capability and the
 * legacy ConnectedUniversalSearch flow had no client-side gate
 * either.
 */
export function parseAdrTerm(term: string): string | null {
  const trimmed = term.trim();
  if (!isValidInternationalPhoneNumber(trimmed)) return null;
  const digits = trimmed.replace(/[^\d]/g, '');
  return digits || null;
}

interface UseAdrSearch {
  /** See {@link parseAdrTerm}. */
  parse: (term: string) => string | null;
  /**
   * Background-fetch state for the most recent ADR kickoff, mirrored
   * from `state.rade.adrLookup`. Same semantics as the MedicAlert
   * channel: `pending` while the request is in flight, `success` /
   * `empty` / `error` once it resolves.
   */
  lookup: RadeLookupState | null;
  /**
   * Kick off a background ADR lookup. Stamps a fresh requestId into
   * `radeSlice.adrLookup` (status = `pending`); the fetch effect in
   * this hook resolves it when the API responds, dropping
   * stale-requestId responses so a fast follow-up search wins.
   *
   * Does NOT open the RAD-e map card — the user has to click the
   * resulting dropdown row for that. See {@link openCard}.
   */
  kickoff: (phoneNumber: string) => void;
  /**
   * Open the RAD-e map card with the most recent ADR lookup's data.
   * Marks `state.rade.selectedLookup = 'adr'` so `RADECardNext` picks
   * up the right channel.
   */
  openCard: () => void;
  /** Hide the RAD-e card and clear the ADR lookup channel. */
  reset: () => void;
}

/**
 * Background-fetch + click-to-open wrapper around the ADR (phone-
 * number RAD-e) lookup. Mirrors `useMedicAlertSearch`, but keyed off
 * `scheme: 'tel'` and gated on phone-number shape rather than a
 * MedicAlert permission.
 */
export default function useAdrSearch(): UseAdrSearch {
  const dispatch = useDispatch();
  const asyncDispatch = dispatch as unknown as EmergencyDataDispatch;
  const lookup = useSelector((state: RootState) => state.rade.adrLookup);

  const kickoff = useCallback(
    (phoneNumber: string) => {
      dispatch(startAdrLookup({ id: phoneNumber }));
    },
    [dispatch],
  );

  const openCard = useCallback(() => {
    dispatch(selectLookup('adr'));
    dispatch(setMapCardData(null));
    dispatch(setCardsToRender(RADE_MAP_CARD));
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch(clearAdrLookup());
    dispatch(closeCard(RADE_MAP_CARD));
  }, [dispatch]);

  // Background fetch driver. Watches the in-flight ADR lookup; on
  // every fresh (`pending`) requestId, fires the API and resolves
  // the slice. The inflight ref drops late responses so a newer
  // lookup wins.
  const inflightRequestIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lookup) {
      inflightRequestIdRef.current = null;
      return;
    }
    if (lookup.status !== 'pending') return;
    if (inflightRequestIdRef.current === lookup.requestId) return;

    const { id, requestId } = lookup;
    inflightRequestIdRef.current = requestId;

    void (async () => {
      try {
        const { payload } = await asyncDispatch(
          fetchEmergencySession(id, {
            attributes: { scheme: 'tel' },
          }),
        );
        if (inflightRequestIdRef.current !== requestId) return;
        const items = normalizeInlineRADE(payload || {}) as RadeCardItem[];
        dispatch(
          resolveAdrLookup({
            requestId,
            status: items && items.length > 0 ? 'success' : 'empty',
            data: items ?? [],
          }),
        );
      } catch (err) {
        if (inflightRequestIdRef.current !== requestId) return;
        // eslint-disable-next-line no-console -- error visibility for ADR fetch failures
        console.error('ADR lookup failed', err);
        dispatch(
          resolveAdrLookup({
            requestId,
            status: 'error',
            data: [],
          }),
        );
      }
    })();
  }, [lookup, asyncDispatch, dispatch]);

  return useMemo(
    () => ({
      parse: parseAdrTerm,
      lookup,
      kickoff,
      openCard,
      reset,
    }),
    [lookup, kickoff, openCard, reset],
  );
}
